import { useState, useEffect, useCallback } from 'react'
import { appsyncQuery } from '../api/appsync-client.js'

const LIST_REPORTS = `
  query ListReports {
    listReports {
      managerId reportId reportEmail reportName status invitedAt tags
    }
  }
`

const LIST_REPORT_SESSIONS = `
  query ListReportSessions($reportId: ID!) {
    listReportSessions(reportId: $reportId) {
      report { reportId reportName }
      sessions {
        sessionId userId managerId status energy scheduledAt
        createdAt updatedAt layout sections agenda readyAt completedAt cancelledAt
      }
    }
  }
`

const ADD_REPORT = `
  mutation AddReport($email: String!) {
    addReport(email: $email) {
      reportId reportEmail reportName status invitedAt tags
    }
  }
`

const UPDATE_REPORT_TAGS = `
  mutation UpdateReportTags($reportId: ID!, $tags: [String!]!) {
    updateReportTags(reportId: $reportId, tags: $tags) {
      reportId tags
    }
  }
`

const REMOVE_REPORT = `
  mutation RemoveReport($reportId: ID!) {
    removeReport(reportId: $reportId)
  }
`

function parseSession(s) {
  if (!s) return s
  if (typeof s.sections === 'string') try { s.sections = JSON.parse(s.sections) } catch { /* keep as-is */ }
  if (typeof s.agenda === 'string') try { s.agenda = JSON.parse(s.agenda) } catch { /* keep as-is */ }
  if (typeof s.goalSnapshot === 'string') try { s.goalSnapshot = JSON.parse(s.goalSnapshot) } catch { /* keep as-is */ }
  return s
}

export function useTeam({ enabled = true } = {}) {
  const [reports, setReports] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await appsyncQuery(LIST_REPORTS)
      setReports(data.listReports)
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { if (enabled) load() }, [load, enabled])

  // Refetch when tab regains focus
  useEffect(() => {
    if (!enabled) return
    const handler = () => { if (document.visibilityState === 'visible') load() }
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [enabled, load])

  const loadReportSessions = useCallback(async (reportId) => {
    const data = await appsyncQuery(LIST_REPORT_SESSIONS, { reportId })
    const result = data.listReportSessions
    if (result.sessions) result.sessions = result.sessions.map(parseSession)
    return result
  }, [])

  const addReport = useCallback(async (email) => {
    const data = await appsyncQuery(ADD_REPORT, { email })
    const resp = data.addReport
    setReports((prev) => [...prev, {
      reportId: resp.reportId,
      reportEmail: resp.reportEmail,
      reportName: resp.reportName,
      addedAt: resp.invitedAt,
      tags: [],
    }])
    return resp
  }, [])

  const updateReportTags = useCallback(async (reportId, tags) => {
    await appsyncQuery(UPDATE_REPORT_TAGS, { reportId, tags })
    setReports((prev) => prev.map((r) => r.reportId === reportId ? { ...r, tags } : r))
  }, [])

  const removeReport = useCallback(async (reportId) => {
    await appsyncQuery(REMOVE_REPORT, { reportId })
    setReports((prev) => prev.filter((r) => r.reportId !== reportId))
  }, [])

  return { reports, loading, error, loadReportSessions, addReport, updateReportTags, removeReport, reload: load }
}
